/**
 * Trigger a browser download of a pre-signed URL via a hidden `<a>`.
 * The URL is signed with `Content-Disposition: attachment`, so the anchor click
 * downloads the file instead of navigating, and the server stays the authority on
 * the filename — no client-owned `download` attribute is set. Anchor clicks
 * (unlike `window.open`) are NOT popup-blocked, so several can fire from one user
 * gesture — which is what package downloads need. No object URL is created or
 * revoked, and the body is never fetched as a Blob. `document`-guarded so it is a
 * no-op outside the browser.
 */
export function triggerBrowserDownload(url: string): void {
	if (typeof document === 'undefined') return
	const a = document.createElement('a')
	a.href = url
	a.rel = 'noopener'
	a.style.display = 'none'
	document.body.appendChild(a)
	a.click()
	a.remove()
}

/** Gap between consecutive anchor clicks — enough that the browser treats each as its own download. */
const DOWNLOAD_STAGGER_MS = 300

/**
 * Hand several already-signed URLs to the browser, one download each, spaced out.
 *
 * Every URL must be signed BEFORE calling this: a network round-trip between two clicks
 * spends the transient user activation the gesture granted, after which the browser may
 * suppress the remaining downloads without telling the page. The stagger is the other half
 * of the same problem — clicks fired in one tick get coalesced or dropped.
 *
 * `null` entries are skipped, so a caller that pre-signs with a per-file `catch` can pass its
 * results straight through. Returns how many downloads were started, which is what a caller
 * may honestly report — a started transfer's outcome is not observable from here.
 */
export async function triggerStaggeredDownloads(urls: readonly (string | null)[]): Promise<number> {
	let started = 0
	for (const url of urls) {
		if (!url) continue
		if (started > 0) await new Promise((resolve) => setTimeout(resolve, DOWNLOAD_STAGGER_MS))
		triggerBrowserDownload(url)
		started += 1
	}
	return started
}
